import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MysticalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  loading?: boolean;
  variant?: 'default' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
}

const MysticalButton = React.forwardRef<HTMLButtonElement, MysticalButtonProps>(
  ({ 
    className, 
    children, 
    loading = false, 
    disabled,
    variant = 'default',
    size = 'md',
    ...props 
  }, ref) => {
    const sizeClasses = {
      sm: 'py-2 px-4 text-xs',
      md: 'py-3 px-6 text-sm',
      lg: 'py-4 px-8 text-base'
    };

    const variantClasses = {
      default: 'bg-gradient-to-br from-white to-gray-50',
      secondary: 'bg-transparent border-gray-400 text-gray-600 hover:border-gray-600'
    };

    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'relative overflow-hidden transition-all duration-300',
          'font-mystical tracking-wider rounded-md uppercase',
          'border-2 border-solid',
          'text-gray-800',
          'hover:shadow-lg hover:shadow-amber-200/20',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          sizeClasses[size],
          variantClasses[variant],
          className
        )}
        style={{
          borderColor: variant === 'secondary' ? '#9CA3AF' : '#1F2937',
          borderWidth: '2px',
          borderStyle: 'solid',
          background: variant === 'secondary' ? 'transparent' : undefined
        }}
        {...props}
      >
        <span className="relative z-10 flex items-center justify-center">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {children}
        </span>
        
        {/* Golden shimmer effect on hover */}
        <span 
          className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-500"
          style={{
            background: 'radial-gradient(circle at center, rgba(217, 119, 6, 0.1) 0%, transparent 70%)'
          }}
        />
      </button>
    );
  }
);

MysticalButton.displayName = 'MysticalButton';

export { MysticalButton };